/// Outboard motor maintenance — RYA Powerboat L2 + Tender course
/// material. Outboards are a distinct beast from inboard diesels:
/// petrol (mostly), 2-stroke or 4-stroke, lighter + simpler.
///
/// Three domains:
///
///   1. Outboard types — 2-stroke vs 4-stroke, salt-water vs fresh.
///   2. Maintenance procedures — spark plug, gear oil, impeller,
///      fuel filter, throttle / shift linkage.
///   3. Kill-cord care — the most important safety device on a fast
///      powerboat.

// ───────────────────────────────────────────────────────────────────
// 1. Outboard types
// ───────────────────────────────────────────────────────────────────

export type OutboardType = "2-stroke" | "4-stroke" | "electric";

export interface OutboardInfo {
  id: OutboardType;
  name: string;
  /// One-line gist.
  summary: string;
  /// Operational characteristics.
  characteristics: string;
  /// Service intervals + complexity.
  servicing: string;
  /// Pros + cons summary.
  proCons: string;
}

export const OUTBOARDS: readonly OutboardInfo[] = Object.freeze([
  {
    id: "2-stroke",
    name: "2-stroke outboard",
    summary:
      "Petrol engine with oil mixed into the fuel (pre-mix OR oil-injection). Combustion every revolution. Lighter + simpler than 4-stroke.",
    characteristics:
      "Higher power-to-weight ratio. Faster throttle response. Burns oil with fuel — visible blue exhaust on start-up + at low throttle. Pre-mix ratio typically 50:1 (modern) or 25:1 (older); always check manufacturer spec.",
    servicing:
      "Spark plug every 100 hrs. Gear oil every 100 hrs OR annually. Water-pump impeller every 100 hrs OR every 2 years. Fewer moving parts than 4-stroke = simpler servicing.",
    proCons:
      "Pros: lighter + cheaper + simpler. Cons: smokier + thirstier (higher fuel + oil consumption per hour) + louder. Many countries are phasing them out for noise + emissions.",
  },
  {
    id: "4-stroke",
    name: "4-stroke outboard",
    summary:
      "Petrol engine with separate oil sump + fuel tank. Combustion every other revolution. Quieter, cleaner + more efficient than 2-stroke.",
    characteristics:
      "Lower fuel consumption per nm. Quieter operation. Sump oil + spark plugs need regular changes like a car engine.",
    servicing:
      "Engine oil + filter every 100 hrs. Spark plugs every 100-200 hrs. Gear oil every 100 hrs. Water-pump impeller every 100 hrs OR every 2 years. More components to maintain than 2-stroke.",
    proCons:
      "Pros: quieter, cleaner, fuel-efficient. Cons: heavier, more expensive, more complex servicing. The dominant outboard type now.",
  },
  {
    id: "electric",
    name: "Electric outboard",
    summary:
      "Brushless DC motor powered by lithium batteries. Silent, no combustion, zero emissions.",
    characteristics:
      "Battery capacity in kWh determines range. Throttle response immediate; no warm-up; no engine noise. Best for tenders + low-speed manoeuvring.",
    servicing:
      "Annual visual inspection. Battery capacity check (replace at 70% capacity loss). Propeller + skeg inspection. No oil, no spark plugs, no fuel filter — minimal maintenance.",
    proCons:
      "Pros: silent, zero emissions, instant torque, near-zero maintenance, no fuel storage on boat. Cons: limited range (kWh-dependent), high upfront cost, slower than equivalent petrol at high speeds.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Maintenance procedures
// ───────────────────────────────────────────────────────────────────

export type OutboardProcedureId =
  | "spark-plug"
  | "gear-oil"
  | "impeller-outboard"
  | "fuel-filter-outboard"
  | "throttle-shift-linkage"
  | "annual-service";

export interface OutboardProcedure {
  id: OutboardProcedureId;
  name: string;
  /// Which outboard types this applies to.
  appliesTo: readonly OutboardType[];
  /// When to do it.
  whenToDo: string;
  /// Tools + parts.
  tools: readonly string[];
  /// Step-by-step procedure.
  steps: readonly string[];
  /// Pitfalls.
  pitfalls: readonly string[];
  /// Duration.
  duration: string;
}

export const OUTBOARD_PROCEDURES: readonly OutboardProcedure[] = Object.freeze([
  {
    id: "spark-plug",
    name: "Spark plug — check or replace",
    appliesTo: ["2-stroke", "4-stroke"],
    whenToDo:
      "Annual check; replace every 100 hours OR every 2 years OR when running rough OR when fouled (black, oily or white).",
    tools: [
      "Spark-plug spanner of the correct size",
      "Feeler gauge (for gap check on 4-stroke)",
      "Wire brush (for cleaning carbon)",
      "Replacement plug (correct part number for the engine)",
      "Plug-gap setting tool",
    ],
    steps: [
      "Engine COLD + STOPPED. Disconnect the kill cord.",
      "Remove the spark-plug cap by pulling firmly (don't tug the lead).",
      "Use the plug spanner to UNSCREW the plug anti-clockwise.",
      "Inspect the electrode end: tan-brown deposits = healthy; black + oily = rich running or 2-stroke oil ratio wrong; white = lean running or air leak; broken porcelain = damage.",
      "If reusing: clean carbon with the wire brush + check gap with feeler gauge.",
      "If replacing: CHECK the new plug's part number matches; set the gap per manufacturer spec.",
      "Hand-start the new plug into the cylinder head (NEVER cross-thread); finish tightening with the spanner.",
      "Reconnect the spark-plug cap.",
      "On a multi-cylinder outboard, repeat for each cylinder.",
    ],
    pitfalls: [
      "Cross-threading the new plug — strips the cylinder head + expensive repair. Always hand-start.",
      "Wrong plug part number — wrong heat range damages the engine.",
      "Wrong gap on a 4-stroke — engine misfires; verify the spec.",
      "Touching the porcelain ends with bare hands — skin oils contaminate. Use a clean cloth.",
    ],
    duration: "10-20 minutes",
  },
  {
    id: "gear-oil",
    name: "Gear oil — change",
    appliesTo: ["2-stroke", "4-stroke"],
    whenToDo:
      "Every 100 hours OR annually OR if water visible in old oil (white + milky = water contamination = SEAL failed).",
    tools: [
      "Marine gear oil (correct grade per maker; typically SAE 80/90 EP)",
      "Drain pan",
      "Screwdriver or socket for drain + fill screws",
      "Replacement sealing washers",
    ],
    steps: [
      "Outboard MOUNTED + tilted DOWN so the lower unit is vertical.",
      "Position a drain pan UNDER the lower unit.",
      "Loosen the upper FILL screw FIRST (acts as a vent).",
      "Loosen the LOWER drain screw — oil drains out.",
      "Wait for oil to drip out completely (5-10 min).",
      "INSPECT the drained oil: smooth + dark = OK; milky + creamy = water; metal flakes = gear / bearing wear.",
      "Refit the lower drain plug FIRST with a fresh sealing washer.",
      "Inject new gear oil via the LOWER drain hole using a special pump or squeeze bottle — keep pumping until oil starts to overflow at the UPPER fill hole.",
      "Quickly install + tighten the upper fill plug.",
      "Snug both plugs but DON'T over-tighten — strips threads.",
      "Wipe clean + check for drips over 24 h.",
    ],
    pitfalls: [
      "Filling from the TOP — air gets trapped + lower unit runs dry. Always fill from the bottom.",
      "Water in old oil — indicates a failed prop-shaft seal; have the seals replaced before the next outing.",
      "Wrong oil grade — gearbox failure in hundreds of hours.",
    ],
    duration: "20-30 minutes",
  },
  {
    id: "impeller-outboard",
    name: "Water-pump impeller — replace",
    appliesTo: ["2-stroke", "4-stroke"],
    whenToDo:
      "Every 100 hours OR every 2 years OR when 'tell-tale' water stream is weak OR engine temperature unusually high.",
    tools: [
      "Spare impeller + impeller housing parts (gasket, key, wear plate if applicable)",
      "Socket set + screwdrivers per the engine",
      "Marine grease",
      "Service manual diagram of the lower-unit assembly",
    ],
    steps: [
      "Engine STOPPED + cold. Disconnect kill cord.",
      "Outboard tilted UP for access. Drain gearbox oil first if you'll be removing the lower unit.",
      "Remove screws holding the lower unit to the upper. Carefully slide the lower unit away — watch the drive shaft + the shift rod which both come away.",
      "Locate the water-pump housing at the top of the lower unit.",
      "Remove housing screws; lift housing off.",
      "Inspect the OLD impeller: vanes worn / cracked / chunks missing = TIME TO REPLACE.",
      "Remove old impeller — lift straight off the drive shaft.",
      "INSPECT the inside of the housing + the wear plate — replace if damaged.",
      "Lubricate the new impeller with marine grease (NOT engine oil — degrades the rubber).",
      "Install new impeller — rotate to bend vanes the correct way before sliding the housing back over the top.",
      "Refit housing screws + reattach the lower unit (watching the drive shaft splines align + the shift rod engages).",
      "Refill gear oil if drained.",
      "Test in water OR on a flushing rig — verify 'tell-tale' stream is strong.",
    ],
    pitfalls: [
      "Wrong vane rotation — impeller vanes must curve in the direction of rotation. Check before re-housing.",
      "Drive shaft splines not aligned — lower unit won't seat; force it + you damage the splines.",
      "Shift rod not engaged — gearbox doesn't shift after refit.",
      "Lubricating with engine oil — destroys the rubber within hours.",
    ],
    duration: "60-120 minutes (intermediate skill)",
  },
  {
    id: "fuel-filter-outboard",
    name: "Fuel filter — clean or replace",
    appliesTo: ["2-stroke", "4-stroke"],
    whenToDo:
      "Annually OR every 100 hours OR if engine running rough at higher throttle.",
    tools: [
      "Replacement filter element OR cleaning brush",
      "Rags + drain pan",
      "Screwdriver or socket for the filter housing",
    ],
    steps: [
      "Engine STOPPED + cold.",
      "Locate the in-line fuel filter (usually a clear plastic bowl on a 2-stroke; a screw-on canister on a 4-stroke).",
      "Place a rag UNDER the filter to catch fuel drips.",
      "Disconnect the fuel lines OR unscrew the filter.",
      "Drain the filter bowl + inspect for water + sediment.",
      "Clean the filter (some are washable) OR replace with new element.",
      "Reinstall + reconnect fuel lines.",
      "Prime the engine (squeeze the primer bulb until firm).",
      "Start + check for leaks.",
    ],
    pitfalls: [
      "Spilled fuel near a hot engine — wait until cold; catch with rags.",
      "Wrong filter — wrong micron rating reduces fuel flow or doesn't filter water.",
      "Failed to prime — engine won't start.",
    ],
    duration: "15-30 minutes",
  },
  {
    id: "throttle-shift-linkage",
    name: "Throttle + shift linkage — inspect + lubricate",
    appliesTo: ["2-stroke", "4-stroke"],
    whenToDo:
      "Annual inspection OR when throttle / shift feels stiff or notchy OR after winter lay-up.",
    tools: [
      "Marine lubricant spray (Teflon-based or marine grease)",
      "Cleaning rag",
      "Visual inspection only — no disassembly normally needed",
    ],
    steps: [
      "Engine STOPPED + cold.",
      "Wipe clean all visible linkage components — throttle cable end + shift cable end + linkage arms.",
      "Lubricate all PIVOT POINTS + cable attachment points with marine lube.",
      "Move throttle through full range + shift between F/N/R — check for smooth operation, no notches or sticking.",
      "Inspect cables for cracks, corrosion, fraying.",
      "Inspect rubber boots covering linkage — replace if cracked or split (allow salt water in + linkage corrodes).",
    ],
    pitfalls: [
      "Using WD-40 only — solvent washes away existing grease + leaves linkage dry. Use proper marine lubricant.",
      "Forcing a notchy throttle — bends cable + makes it worse.",
      "Ignoring split rubber boots — internal corrosion in salt environment ruins linkage in months.",
    ],
    duration: "10-20 minutes",
  },
  {
    id: "annual-service",
    name: "Annual service — full",
    appliesTo: ["2-stroke", "4-stroke"],
    whenToDo:
      "End of season OR at the 100-hour mark. Combines all the above procedures plus a few additional checks.",
    tools: [
      "All tools + parts from the procedures above",
      "Anti-fouling for the lower unit (if not previously applied)",
      "Touch-up paint for any chips",
      "Spark-plug spare for emergency replacement",
    ],
    steps: [
      "Change gear oil (procedure above) — inspect old oil for water + metal.",
      "Change spark plugs.",
      "Replace water-pump impeller.",
      "Clean / replace fuel filter.",
      "Inspect + lubricate throttle + shift linkage.",
      "Inspect the propeller — replace if damaged.",
      "Inspect anodes (zinc / aluminium) — replace if eroded > 50%.",
      "Test the kill cord switch — engine should STOP immediately when the lanyard is pulled.",
      "Test charging system — voltage rising to 14 V at cruising rpm.",
      "Run the engine on a flushing rig with fresh water for 5-10 minutes after the service.",
      "Note hours-meter + log everything done.",
    ],
    pitfalls: [
      "Skipping the anode check — anode failure = corrosion of expensive lower-unit components.",
      "Not testing the kill cord — false sense of safety until the day it doesn't work.",
      "Not flushing with fresh water at the end of the service — salt deposits left to dry destroy the cooling passages.",
    ],
    duration: "3-4 hours (basic-to-intermediate skill)",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 3. Kill-cord care
// ───────────────────────────────────────────────────────────────────

export const KILL_CORD_RULES: readonly string[] = Object.freeze([
  "ALWAYS wear the kill cord when the engine is running. NOT a 'might wear it for fast bits' — ALWAYS.",
  "Attach the cord around the leg (above the knee) OR to a bridge of the lifejacket. NOT to a belt or wristband.",
  "If multiple crew can drive the boat, swap the kill cord between them as the wheel changes hands.",
  "Test the kill cord WEEKLY — start the engine, pull the cord, the engine should STOP immediately. Reset + start again.",
  "Inspect for damage — frayed cord, cracked clip, faded colour. Replace at first sign of wear (cheap insurance).",
  "When boarding from the water, attach the kill cord BEFORE starting the engine — never start with the lanyard in your hand.",
  "Cord must be SHORT enough that pulling it on a fall actually disconnects (typical 60 cm).",
  "Modern wireless kill cords (Mercury / Yamaha) — same principles but electronics-based. Check battery + range monthly.",
  "Never bypass the kill cord with a 'cheater' clip — this is a critical safety device. Multiple fatalities have occurred because the kill cord was bypassed.",
]);
